import { getBoolFromBitValue } from '../../../common/utils/helpers';
import { ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY, ATLASSIAN_CURRENT_LOCALE_COOKIE_KEY } from '../../../constants';
import { checkIfConsentsAreStale, checkIfTokenHasAConsentLocale, isLocaleCoveredByAnyPrivacyActs, isLocaleCoveredByTier1Policies, isLocaleCoveredByTier2Policies } from '../../../services/consent-hub-service/utils';
import { getCookie } from '../../cookie-controls/get-cookie';
export var isCurrentLocaleStrongerThanConsentedLocale = function isCurrentLocaleStrongerThanConsentedLocale(_ref) {
  var consentToken = _ref.consentToken,
    currentLocale = _ref.currentLocale;
  if (!currentLocale || currentLocale === 'UNKNOWN') {
    return true; // If we don't know the locale, we should show the banner, so consider it stronger
  }
  var consentedUnderGDPR = !!Number(consentToken[8]);
  var consentedUnderCPRA = !!Number(consentToken[9]);
  var consentedUnderLGPD = !!Number(consentToken[10]);
  var consentedUnderFADP = !!Number(consentToken[11]);
  var userConsentedInTier1 = consentedUnderGDPR || consentedUnderLGPD || consentedUnderFADP;
  var userConsentedInTier2 = consentedUnderCPRA;
  var currentLocaleInTier1 = isLocaleCoveredByTier1Policies(currentLocale);
  var currentLocaleInTier2 = isLocaleCoveredByTier2Policies(currentLocale);

  // Tier 1, strong privacy regulations
  if (currentLocaleInTier1) {
    // If user is within Tier 1 locale, but hasn't consented in Tier 1, then currentLocale must be stronger
    // If they have consented in Tier 1, currentLocale is equivalent (don't show banner)
    var currentLocaleStronger = !userConsentedInTier1;
    return currentLocaleStronger;
  }

  // Tier 2, weaker privacy regulations
  if (currentLocaleInTier2) {
    if (!userConsentedInTier2) {
      // If they haven't consented in this tier, but consentedInT1, currentLocale is weaker. consentedInT3, it's stronger
      var _currentLocaleStronger = !userConsentedInTier1;
      return _currentLocaleStronger;
    }
    // If the user has consented in Tier 2, currentLocale is equivalent (don't show banner)
    return false;
  }

  // Tier 3, weakest privacy regulations
  // If we're not in any other tier, we must be in a region with no privacy regulations
  var consentedInStrongerTier = userConsentedInTier1 || userConsentedInTier2;
  if (consentedInStrongerTier) {
    // If user has made consents somewhere (with laws), but are currently outside of any regional protection laws
    // therefore, the currentLocale must be weaker (don't show banner)
    return false;
  }
  // If they've never consented under any regional protection laws (in any tier), they're either default or somehow consented outside of protection laws
  // The currentLocale doesn't have any protections, so we should hide the banner anyway, e.g. consider currentLocale weaker
  return false;
};
export var getBannerVisibilityByLocale = function getBannerVisibilityByLocale(_ref2) {
  var consentToken = _ref2.consentToken,
    currentLocale = _ref2.currentLocale,
    consentTimestamp = _ref2.consentTimestamp,
    fallbackConsentToken = _ref2.fallbackConsentToken;
  // Best effort to populate all these values if they're missing
  var fallbackConsents = fallbackConsentToken || getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY);
  var locale = currentLocale || getCookie(ATLASSIAN_CURRENT_LOCALE_COOKIE_KEY);
  var localeIsValid = locale && locale !== 'UNKNOWN';
  if (localeIsValid) {
    // We have consent token. Consents are either default, or legitimate (local only or persisted).

    var consentDataUnavailableBit = consentToken[7]; // Logged out users will have this bit set to 1
    var isLoggedOutConsent = getBoolFromBitValue(consentDataUnavailableBit);
    var userConsentedUnderPrivacyProtections = checkIfTokenHasAConsentLocale(consentToken);
    // If user has consented somewhere, but can't save to ConsentHub (unavailable bit), this is a local-only user
    var localOnlyConsent = isLoggedOutConsent && userConsentedUnderPrivacyProtections;
    // If user hasn't consented with privacy laws, and they're logged in, this is a default consent
    var isDefaultAuthenticatedConsent = !isLoggedOutConsent && !userConsentedUnderPrivacyProtections;
    // If user hasn't consented with privacy laws, and they're logged out, this is a default consent
    var isDefaultUnauthenticatedConsent = isLoggedOutConsent && !userConsentedUnderPrivacyProtections;
    var localeWarrantsBanner = isLocaleCoveredByAnyPrivacyActs(locale);

    // Unauthed/logged out user flow
    if (isDefaultUnauthenticatedConsent) {
      // If it's a default, unauthed, set of consents, lacking a fallback
      // we should hide/show depending on locale, as we know they've never consented in the past year

      // However, if we defaults + fallback, then we may have a discrepancy where ConsentHub is unavailable or they're logged out
      // but we have consented previously (which is the only way to save to the fallback)
      if (fallbackConsents) {
        var fallbackUnavailableBit = fallbackConsents[7];
        var isUnauthenticatedFallbackConsent = getBoolFromBitValue(fallbackUnavailableBit);
        var fallbackConsentedSomewhere = checkIfTokenHasAConsentLocale(fallbackConsents);

        // Check if the fallback is from a logged out consent (localOnlyConsent)—
        // if so, they're logged out and saved while logged out, only show if they're in a stronger region than when they consented
        var _localOnlyConsent = isUnauthenticatedFallbackConsent && fallbackConsentedSomewhere;
        if (_localOnlyConsent) {
          var _showBannerIfCurrentLocaleIsStronger = isCurrentLocaleStrongerThanConsentedLocale({
            consentToken: fallbackConsents,
            currentLocale: locale
          });
          return _showBannerIfCurrentLocaleIsStronger;
        }
        // Or if it is from a logged in consent, and they've consented somewhere (they must have)
        var loggedOutUserPreviouslyConsentedWhileLoggedIn = !isUnauthenticatedFallbackConsent && fallbackConsentedSomewhere;
        if (loggedOutUserPreviouslyConsentedWhileLoggedIn) {
          var _showBannerIfCurrentLocaleIsStronger2 = isCurrentLocaleStrongerThanConsentedLocale({
            consentToken: fallbackConsents,
            currentLocale: locale
          });
          return _showBannerIfCurrentLocaleIsStronger2;
        }
      }
      return localeWarrantsBanner;
    }

    // Authed/logged in user flow
    if (isDefaultAuthenticatedConsent) {
      // If it's a default, authed, set of consents, lacking a fallback
      // we should hide/show depending on locale, as we know they've never consented in the past year
      // However, if we defaults + fallback, defer to locale requirements because we can actually compare against locales
      if (fallbackConsents) {
        var _fallbackUnavailableBit = fallbackConsents[7];
        var _isUnauthenticatedFallbackConsent = getBoolFromBitValue(_fallbackUnavailableBit);
        var _fallbackConsentedSomewhere = checkIfTokenHasAConsentLocale(fallbackConsents);
        var loggedInUserPreviouslyConsentedWhileLoggedOut = _isUnauthenticatedFallbackConsent && _fallbackConsentedSomewhere;
        if (loggedInUserPreviouslyConsentedWhileLoggedOut) {
          // They've saved a fallback while logged out (fallback bit is 1), then fetched from CH,
          // which sets authed consent defaults (bit 0)
          // However, we don't want to use defaults here, because they've consented in the past year
          // and we don't want to inadvertently show them a banner again. Instead, lets run the strength check with the fallback
          var _showBannerIfCurrentLocaleIsStronger3 = isCurrentLocaleStrongerThanConsentedLocale({
            consentToken: fallbackConsents,
            currentLocale: locale
          });
          return _showBannerIfCurrentLocaleIsStronger3;
        }
      }
      return localeWarrantsBanner;
    }
    var showBannerIfCurrentLocaleIsStronger = isCurrentLocaleStrongerThanConsentedLocale({
      consentToken: consentToken,
      currentLocale: locale
    });
    if (localOnlyConsent) {
      // User has consented previously, compare regional strength, and then check if the fallback exists (consented within a year)
      // to determine if we should show the banner
      if (!showBannerIfCurrentLocaleIsStronger) {
        // If they're a localOnly user, they will not have a timestamp to validate against
        // So if fallback cookie exists, they've consented in the past year, do not show
        // If it does not exist, we should show the banner, because they're stale
        return fallbackConsents ? false : true;
      }
      // Forcibly show the banner because we're in a stronger locale
      return true;
    }

    // If we're a logged in user (!isLoggedOutConsent && userConsentedUnderPrivacyProtections;)
    // We're not default or local-only, so we should be running normal logic as these are legitimate consents

    // If currentLocale is stronger (>>) than consentedLocale, we would show the banner
    // even if the timestamp is within a year
    // If currentLocale is weaker (<<) than consentedLocale or equivalent, we would not show the banner
    // Unless the timestamp is over a year old, e.g. consents are stale

    if (!showBannerIfCurrentLocaleIsStronger) {
      // If we don't, we can check the existence of the fallback (1yr) cookie
      if (!consentTimestamp) {
        // We could assume since they've consented recently, we don't have to show the banner if this is missing but, to be safe we should
        return fallbackConsents ? false : true;
      }
      // If we have a timestamp to check against, do it, ensure it's not stale. Show banner if it is.
      return checkIfConsentsAreStale(consentTimestamp);
    }
    // Forcibly show the banner because we're in a stronger locale
    return true;
  }

  // If we're here, we cant find a locale to compare with, or it's here and UNKNOWN
  // To be as compliant as possible, we should show the banner regardless of if they've consented or not
  // The risk here is somebody who does not need to consent gets show the banner inadvertently.
  return true;
};